import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import type { ProjectGroupContext, ReportCategory, ReportGenerationParams } from '../../shared/types';
import ReportPreview from '../shared/components/Preview';
import * as ReportGenerationStore from '../../../../../redux/reportGeneration';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption, IncludeFixPlanFilterOption } from '../shared/types';
import { getPreviewURL } from './utils';
import ReportStatus from '../shared/components/Preview/ReportStatus';
import { window } from '../../../../../globals';
import { Action, ResourceType } from '../../../../../../typings/permissions';
import { SBOM_PORTAL } from '../../../../../../shared/featureFlags/flagNames';
import { FeatureFlagRequired } from '../../../../generic';
import { ProjectType } from '../utils';

type Props = {
  params: ReportGenerationParams;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  fixPlanOptions: IncludeFixPlanFilterOption[];
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
};

const Preview: VoidFunctionComponent<Props> = ({
  params: { report_type: reportType },
  format,
  includeOptions,
  dependencyMetadataOptions,
  fixPlanOptions,
  authorName,
  authorEmail,
  supplier,
}: Props) => {
  const reportContext = useSelector(ReportGenerationStore.selectors.getReportContext) as ProjectGroupContext;
  const hasPermissionToEditPortalSettings = window.hasPermission({
    resourceType: ResourceType.OrganizationPortalReports,
    action: Action.Manage,
  });
  const endpoint = getPreviewURL({
    groupId: reportContext.groupId,
    releaseId: reportContext.releaseId,
    reportType,
    format,
    includeOptions,
    dependencyMetadataOptions,
    fixPlanOptions,
    authorName,
    authorEmail,
    supplier,
  });

  return (
    <ReportPreview
      reportType={reportType as ReportCategory}
      previewURL={endpoint}
      format={format}
      includeOptions={includeOptions}
      dependencyMetadataOptions={dependencyMetadataOptions}
      publishStatus={(
        reportContext.projectType === ProjectType.ReleaseGroup &&
          <FeatureFlagRequired flagName={SBOM_PORTAL}>
            {hasPermissionToEditPortalSettings && <ReportStatus format={format} reportType={reportType} />}
          </FeatureFlagRequired>
      )}
    />
  );
};

export default Preview;
